<template>
  <div class="home-page">
    <div class="page-backdrop" aria-hidden="true">
      <img :src="background" alt="" class="page-bg-img" />
      <div class="page-bg-overlay" />
    </div>

    <v-container fluid class="home-content pa-4 pa-sm-6">
      <header class="home-header glass-panel">
        <div class="brand-wrap">
          <img :src="logo" alt="Wall Labs logo" class="brand-logo" />
          <div class="brand-text">
            <h1 class="brand-title">
              <span class="brand-wall">Wall</span>
              <span class="brand-labs">Labs</span>
            </h1>
          </div>
        </div>
        <div class="header-actions">
          <v-btn
            class="upload-btn"
            variant="flat"
            rounded="xl"
            @click="openUploadDialog"
          >
            <v-icon icon="mdi-cloud-upload-outline" start size="20" />
            Upload Wallpaper
          </v-btn>
          <v-btn
            class="logout-btn"
            variant="outlined"
            rounded="xl"
            @click="goToLogin"
          >
            Logout
          </v-btn>
        </div>
      </header>

      <section class="chip-section glass-panel">
        <div class="chip-row">
          <v-chip
            v-for="category in categories"
            :key="category"
            :class="[
              'chip-item',
              selectedCategory === category ? 'chip-item--active' : ''
            ]"
            rounded="xl"
            @click="selectedCategory = category"
          >
            {{ category }}
          </v-chip>
        </div>
      </section>

      <div v-if="loading" class="status-row glass-panel">
        <v-progress-circular indeterminate color="#a855f7" size="28" />
        <span>Loading wallpapers...</span>
      </div>

      <v-alert
        v-else-if="errorMessage"
        type="error"
        variant="tonal"
        class="mb-4 glass-alert"
        density="comfortable"
        rounded="xl"
      >
        {{ errorMessage }}
      </v-alert>

    <div
      v-else-if="filteredWallpapers.length > 0"
      class="grid-wrap"
    >
      <v-card
        v-for="wallpaper in filteredWallpapers"
        :key="wallpaper.id"
        class="wall-card"
        :class="{ 'wall-card--active': wallpaper.id === activeWallpaperId }"
        elevation="0"
        @click="openPreview(wallpaper)"
      >
        <v-chip
          v-if="wallpaper.id === activeWallpaperId"
          class="active-chip"
          color="success"
          size="small"
        >
          Active
        </v-chip>
        <v-img
          :src="wallpaper.imageUrl"
          :alt="wallpaper.title"
          cover
          height="260"
          class="wall-image"
        />
        <div class="wall-overlay" />
        <div class="wall-text">
          <h3>{{ wallpaper.title }}</h3>
          <p>{{ wallpaper.category }} </p>
      
        </div>
      </v-card>
    </div>

    <v-alert
      v-else
      type="info"
      variant="tonal"
      density="comfortable"
      class="mt-4 glass-alert"
      rounded="xl"
    >
      No wallpapers found for {{ selectedCategory }}.
    </v-alert>

    <WallpaperPreviewDialog
      v-model="previewOpen"
      :wallpaper="selectedWallpaper"
      :downloading="!!downloadingId"
      @download="downloadFromPreview"
    />

    <!-- Upload wallpaper dialog -->
    <v-dialog v-model="uploadOpen" max-width="560" persistent>
      <v-card class="upload-card" rounded="xl">
        <div class="upload-header">
          <div class="upload-title-icon" aria-hidden="true">
            <v-icon icon="mdi-image-plus" size="22" color="#fff" />
          </div>
          <div class="upload-header-text">
            <v-card-title class="upload-title pa-0">Add Wallpaper</v-card-title>
            <p class="upload-hint">
              Pick an image or paste a URL. The image URL is saved and shown in the list.
            </p>
          </div>
        </div>

        <v-card-text class="upload-card-text">
          <v-form ref="uploadFormRef" class="upload-form-fields" @submit.prevent="submitUpload">
            <v-text-field
              v-model="uploadForm.wallpaper_name"
              label="Wallpaper name"
              placeholder="Wallpaper name"
              variant="outlined"
              density="comfortable"
              rounded="lg"
              hide-details="auto"
              bg-color="#ffffff"
              class="upload-field"
              :rules="[rules.required]"
              @update:model-value="syncUrlFromNameAndFile"
            />
            <v-select
              v-model="uploadForm.category"
              :items="uploadCategories"
              label="Category"
              variant="outlined"
              density="comfortable"
              rounded="lg"
              hide-details="auto"
              bg-color="#ffffff"
              class="upload-field"
              :rules="[rules.required]"
            />
            <v-file-input
              v-model="uploadFile"
              label="Choose image"
              placeholder="Choose image"
              accept="image/png,image/jpeg,image/webp"
              variant="outlined"
              density="comfortable"
              rounded="lg"
              hide-details="auto"
              bg-color="#ffffff"
              prepend-icon=""
              prepend-inner-icon="mdi-paperclip"
              class="upload-field upload-field--file"
              show-size
              @update:model-value="onUploadFileChange"
            />
            <v-img
              v-if="uploadPreviewSrc"
              :src="uploadPreviewSrc"
              max-height="160"
              contain
              class="upload-preview"
            />
            <!-- <v-text-field
              v-model="uploadForm.s3_url"
              label="Image URL"
              variant="outlined"
              density="comfortable"
              :rules="[rules.required, rules.url]"
              hint="Auto-filled from image name; edit if your S3 path is different"
              persistent-hint
            /> -->
          
            <v-alert
              v-if="uploadError"
              type="error"
              variant="tonal"
              density="compact"
              class="mt-2"
            >
              {{ uploadError }}
            </v-alert>
          </v-form>
        </v-card-text>
        <v-card-actions class="upload-actions">
          <v-btn variant="text" :disabled="uploading" @click="closeUploadDialog">
            Cancel
          </v-btn>
          <v-spacer />
          <v-btn
            class="primary-gradient-btn"
            variant="flat"
            rounded="xl"
            :loading="uploading"
            @click="submitUpload"
          >
            Add to catalog
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snackbar"
      :timeout="4000"
      class="home-snackbar"
      rounded="lg"
    >
      {{ snackbarMessage }}
    </v-snackbar>
    </v-container>
  </div>
</template>

<script>
import logo from '@/assets/wall-labs-logo.png'
import background from '@/assets/login-background.png'
import WallpaperPreviewDialog from '@/components/WallpaperPreviewDialog.vue'
import {
  addWallpaper,
  listWallpapers
} from '@/services/wallLabsApi'
import {
  buildS3Key,
  getPublicS3Url,
  uploadToS3
} from '@/services/publicS3Upload'
import {
  getAllLocalWallpaperRecords,
  recordToObjectUrl,
  saveLocalWallpaperImage
} from '@/services/localWallpaperStore'

const STORAGE_KEY = 'wallLabsActiveWallpaper'
const CATEGORIES = [
  'All',
  'Anime-style',
  'Nature',
  'AMOLED',
  'Abstract',
  'Gaming',
  'Sci-Fi'
]

function parseResponsePayload(payload) {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.wallpapers)) return payload.wallpapers
  if (Array.isArray(payload?.Wallpapers)) return payload.Wallpapers
  if (Array.isArray(payload?.items)) return payload.items
  if (Array.isArray(payload?.data)) return payload.data
  if (typeof payload?.body === 'string') {
    try {
      const parsed = JSON.parse(payload.body)
      return Array.isArray(parsed)
        ? parsed
        : parsed?.wallpapers || parsed?.Wallpapers || parsed?.items || parsed?.data || []
    } catch {
      return []
    }
  } 
  return []
}

function normalizeCategory(rawCategory) {
  const value = String(rawCategory || '').trim()
  if (CATEGORIES.includes(value)) return value

  const lower = value.toLowerCase()
  if (lower.includes('anime')) return 'Anime-style'
  if (lower.includes('nature')) return 'Nature'
  if (lower.includes('amoled')) return 'AMOLED'
  if (lower.includes('abstract')) return 'Abstract'
  if (lower.includes('gaming') || lower.includes('game')) return 'Gaming'
  if (lower.includes('sci') || lower.includes('cyber')) return 'Sci-Fi'
  return value || 'Abstract'
}

function normalizeWallpaper(item, index) {
  const imageUrl = String(
    item.s3_url ||
      item.s3Url ||
      item.imageUrl ||
      item.image ||
      item.url ||
      item.wallpaperUrl ||
      ''
  ).trim()

  return {
    id: item.wallpaper_id || item.wallpaperId || item.id || item._id || `wall-${index}`,
    title: item.wallpaper_name || item.title || item.name || `Wallpaper ${index + 1}`,
    imageUrl,
    category: normalizeCategory(item.category || item.type || item.style || item.tag),
    status: String(item.status || '').toUpperCase()
  }
}

function getFetchableImageUrl(imageUrl) {
  const url = String(imageUrl || '').trim()
  if (!url) return ''

  if (process.env.NODE_ENV === 'development') {
    try {
      const parsed = new URL(url)
          if (
            parsed.hostname.includes('walllabs-bucket.s3.amazonaws.com') ||
            parsed.hostname.includes('walllabs-bucket.s3.us-east-1.amazonaws.com')
          ) {
        return `/api-image${parsed.pathname}${parsed.search}`
      }
    } catch {
      return url
    }
  }

  return url
}

function dedupeWallpapers(items) {
  const byId = new Map()
  for (const item of items) {
    byId.set(item.id, item)
  }
  return [...byId.values()]
}

export default {
  name: 'LandingPage',
  components: {
    WallpaperPreviewDialog
  },
  data() {
    return {
      logo,
      background,
      categories: CATEGORIES,
      selectedCategory: 'All',
      wallpapers: [],
      loading: false,
      errorMessage: '',
      previewOpen: false,
      selectedWallpaper: null,
      activeWallpaper: null,
      activeWallpaperId: null,
      downloadingId: null,
      snackbar: false,
      snackbarMessage: '',
      uploadOpen: false,
      uploading: false,
      uploadError: '',
      uploadFile: null,
      uploadPreview: '',
      localObjectUrls: [],
      uploadForm: {
        wallpaper_name: '',
        category: 'Nature',
        s3_url: ''
      },
      rules: {
        required: (v) => !!String(v || '').trim() || 'Required',
        url: (v) =>
          /^https?:\/\/.+/i.test(String(v || '').trim()) || 'Enter a valid URL'
      }
    }
  },
  computed: {
    uploadCategories() {
      return CATEGORIES.filter((c) => c !== 'All')
    },
    filteredWallpapers() {
      const list = Array.isArray(this.wallpapers) ? this.wallpapers : []
      if (this.selectedCategory === 'All') {
        return list
      }
      return list.filter(
        (wallpaper) => wallpaper.category === this.selectedCategory
      )
    },
    uploadPreviewSrc() {
      if (this.uploadPreview) return this.uploadPreview
      const url = String(this.uploadForm.s3_url || '').trim()
      return /^https?:\/\/.+/i.test(url) ? url : ''
    }
  },
  watch: {
    previewOpen(isOpen) {
      if (!isOpen) {
        this.selectedWallpaper = null
      }
    }
  },
  mounted() {
    this.loadActiveWallpaper()
    this.fetchWallpapers()
  },
  methods: {
    goToLogin() {
      this.$router.push('/')
    },
    loadActiveWallpaper() {
      try {
        const saved = localStorage.getItem(STORAGE_KEY)
        if (!saved) return
        const parsed = JSON.parse(saved)
        if (parsed?.id && parsed?.imageUrl) {
          this.activeWallpaper = parsed
          this.activeWallpaperId = parsed.id
        }
      } catch {
        localStorage.removeItem(STORAGE_KEY)
      }
    },
    openPreview(wallpaper) {
      if (!wallpaper) return
      this.selectedWallpaper = wallpaper
      this.previewOpen = true
    },
    async downloadFromPreview(wallpaper) {
      const item = wallpaper || this.selectedWallpaper
      if (!item || this.downloadingId) return

      this.downloadingId = item.id
      try {
        const openedInTab = await this.downloadWallpaperImage(item)
        this.snackbarMessage = openedInTab
          ? `"${item.title}" opened — right-click and Save image as.`
          : `"${item.title}" downloaded.`
        this.snackbar = true
      } catch {
        this.snackbarMessage =
          'Could not download. Check that the image URL is public.'
        this.snackbar = true
      } finally {
        this.downloadingId = null
      }
    },
    async downloadWallpaperImage(wallpaper) {
      const directUrl = String(wallpaper?.imageUrl || '').trim()
      if (!directUrl) throw new Error('No image URL')

      const fetchUrl = getFetchableImageUrl(directUrl)
      const safeName =
        String(wallpaper.title || 'wallpaper').replace(/[^\w\s-]/g, '').trim() ||
        'wallpaper'
      const extension =
        directUrl.match(/\.(png|jpe?g|webp|gif)$/i)?.[1]?.toLowerCase() || 'png'

      let response
      try {
        response = await fetch(fetchUrl)
      } catch {
        response = await fetch(directUrl)
      }

      if (!response.ok) {
        window.open(directUrl, '_blank', 'noopener,noreferrer')
        return true
      }

      const blob = await response.blob()
      const blobUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = `${safeName}.${extension}`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(blobUrl)
      return false
    },
    openUploadDialog() {
      this.uploadError = ''
      this.uploadOpen = true
    },
    closeUploadDialog() {
      this.uploadOpen = false
      this.uploading = false
      this.uploadError = ''
      this.uploadFile = null
      this.revokeUploadPreview()
      this.uploadForm = {
        wallpaper_name: '',
        category: 'Nature',
        s3_url: ''
      }
    },
    revokeUploadPreview() {
      if (this.uploadPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(this.uploadPreview)
      }
      this.uploadPreview = ''
    },
    getSelectedFile() {
      const file = Array.isArray(this.uploadFile)
        ? this.uploadFile[0]
        : this.uploadFile
      return file || null
    },
    syncUrlFromNameAndFile() {
      if (!this.uploadForm.wallpaper_name) return
      const file = this.getSelectedFile()
      const key = buildS3Key(this.uploadForm.wallpaper_name, file)
      this.uploadForm.s3_url = getPublicS3Url(key)
    },
    onUploadFileChange(files) {
      this.revokeUploadPreview()
      const file = Array.isArray(files) ? files[0] : files
      if (!file) return

      if (!this.uploadForm.wallpaper_name) {
        this.uploadForm.wallpaper_name = file.name.replace(/\.[^.]+$/, '')
      }

      this.uploadPreview = URL.createObjectURL(file)
      this.syncUrlFromNameAndFile()
    },
    revokeLocalObjectUrls() {
      for (const url of this.localObjectUrls) {
        if (url?.startsWith('blob:')) {
          URL.revokeObjectURL(url)
        }
      }
      this.localObjectUrls = []
    },
    async enrichWallpapersWithLocalImages(wallpapers) {
      this.revokeLocalObjectUrls()
      const records = await getAllLocalWallpaperRecords()
      const localUrlById = {}

      for (const record of records) {
        const objectUrl = recordToObjectUrl(record)
        if (objectUrl) {
          localUrlById[record.id] = objectUrl
          this.localObjectUrls.push(objectUrl)
        }
      }

      return wallpapers.map((wallpaper) => {
        const localUrl = localUrlById[wallpaper.id]
        if (!localUrl) return wallpaper
        return { ...wallpaper, imageUrl: localUrl }
      })
    },
    async submitUpload() {
      this.uploadError = ''
      const name = this.uploadForm.wallpaper_name.trim()
      const category = this.uploadForm.category
      const file = this.getSelectedFile()
      const s3Key = buildS3Key(name, file)
      const s3Url = getPublicS3Url(s3Key)
      this.uploadForm.s3_url = s3Url

      if (!name || !category) {
        this.uploadError = 'Name and category are required.'
        return
      }

      if (!file) {
        this.uploadError = 'Choose an image file.'
        return
      }

      this.uploading = true
      try {
        const uploadedUrl = await uploadToS3(file, s3Key)

        const result = await addWallpaper({
          wallpaper_name: name,
          category,
          s3_url: uploadedUrl
        })

        const wallpaperId = result?.wallpaper_id
        if (wallpaperId && file) {
          await saveLocalWallpaperImage(wallpaperId, file)
        }

        const newWallpaper = {
          id: wallpaperId || `local-${Date.now()}`,
          title: name,
          imageUrl: uploadedUrl,
          category,
          status: 'LIVE'
        }

        this.selectedCategory = 'All'
        this.closeUploadDialog()
        await this.fetchWallpapers()

        const exists = this.wallpapers.some((w) => w.id === newWallpaper.id)
        if (!exists && newWallpaper.id) {
          const enriched = await this.enrichWallpapersWithLocalImages([
            newWallpaper,
            ...this.wallpapers
          ])
          this.wallpapers = dedupeWallpapers(enriched)
        }

        this.snackbarMessage = `"${name}" uploaded and added. URL: ${uploadedUrl}`
        this.snackbar = true
      } catch (error) {
        this.uploadError = error.message || 'Failed to add wallpaper.'
      } finally {
        this.uploading = false
      }
    },
    async fetchWallpapers() {
      this.loading = true
      this.errorMessage = ''

      try {
        const rawData = await listWallpapers()
        const wallpapers = parseResponsePayload(rawData)

        const normalized = dedupeWallpapers(
          wallpapers
            .map(normalizeWallpaper)
            .filter((item) => item.status === 'LIVE')
            .filter((item) => item.imageUrl)
        )

        this.wallpapers = await this.enrichWallpapersWithLocalImages(normalized)
      } catch (error) {
        this.errorMessage = error.message || 'Failed to load wallpapers.'
        this.wallpapers = []
      } finally {
        this.loading = false
      }
    }
  },
  beforeUnmount() {
    this.revokeUploadPreview()
    this.revokeLocalObjectUrls()
  }
}
</script>

<style scoped>
.home-page {
  position: relative;
  min-height: 100vh;
  color: #f8fafc;
}

.page-backdrop {
  position: fixed;
  inset: 0;
  z-index: 0;
  background: #060b1a;
}

.page-bg-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.page-bg-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(6, 11, 26, 0.55) 0%,
    rgba(6, 11, 26, 0.72) 50%,
    rgba(6, 11, 26, 0.85) 100%
  );
  pointer-events: none;
}

.home-content {
  position: relative;
  z-index: 1;
  max-width: 1400px;
  margin: 0 auto;
}

.glass-panel {
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
}

.home-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  padding: 18px 22px;
  margin-bottom: 20px;
}

.brand-wrap {
  display: flex;
  align-items: center;
  gap: 14px;
}

.brand-logo {
  width: 44px;
  height: 44px;
  object-fit: contain;
  flex-shrink: 0;
}

.brand-title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 800;
  line-height: 1.1;
  display: flex;
  align-items: baseline;
}

.brand-wall {
  background: linear-gradient(135deg, #a855f7 0%, #6366f1 50%, #3b82f6 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.brand-labs {
  color: #f8fafc;
  margin-left: 0.12em;
}

.brand-sub {
  margin: 4px 0 0;
  font-size: 0.8125rem;
  color: rgba(226, 232, 240, 0.75);
  font-weight: 500;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.upload-btn {
  background: linear-gradient(90deg, #3b82f6 0%, #8b5cf6 55%, #a855f7 100%) !important;
  color: #fff !important;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0.02em;
  box-shadow: 0 6px 20px rgba(99, 102, 241, 0.4) !important;
}

.logout-btn {
  border-color: rgba(255, 255, 255, 0.35) !important;
  color: #e2e8f0 !important;
  text-transform: none;
  font-weight: 500;
}

.chip-section {
  padding: 14px 18px;
  margin-bottom: 22px;
}

.chip-row {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 2px;
  scrollbar-width: thin;
}

.chip-item {
  flex: 0 0 auto;
  background: rgba(255, 255, 255, 0.06) !important;
  color: #cbd5e1 !important;
  border: 1px solid rgba(255, 255, 255, 0.15) !important;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.chip-item:hover {
  background: rgba(255, 255, 255, 0.12) !important;
  color: #f1f5f9 !important;
}

.chip-item--active {
  background: linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%) !important;
  color: #fff !important;
  border-color: transparent !important;
  box-shadow: 0 4px 14px rgba(99, 102, 241, 0.45);
}

.status-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  color: #e2e8f0;
  margin: 24px 0;
  padding: 28px;
  font-weight: 500;
}

.glass-alert {
  background: rgba(255, 255, 255, 0.92) !important;
}

.grid-wrap {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 18px;
}

.wall-card {
  border-radius: 18px;
  overflow: hidden;
  position: relative;
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
  transition:
    transform 0.25s ease,
    box-shadow 0.25s ease,
    border-color 0.25s ease;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
}

.wall-card:hover {
  transform: translateY(-6px);
  box-shadow:
    0 16px 40px rgba(0, 0, 0, 0.4),
    0 0 24px rgba(99, 102, 241, 0.2);
  border-color: rgba(139, 92, 246, 0.45);
}

.wall-card--active {
  outline: 2px solid #22c55e;
  outline-offset: 2px;
  box-shadow:
    0 12px 32px rgba(0, 0, 0, 0.35),
    0 0 20px rgba(34, 197, 94, 0.25);
}

.active-chip {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 3;
  font-weight: 600;
}

.wall-image {
  filter: saturate(1.08) contrast(1.02);
}

.wall-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(6, 11, 26, 0.92) 0%,
    rgba(6, 11, 26, 0.25) 55%,
    transparent 100%
  );
  pointer-events: none;
}

.wall-text {
  position: absolute;
  left: 14px;
  right: 14px;
  bottom: 14px;
  z-index: 2;
  pointer-events: none;
}

.wall-text h3 {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  line-height: 1.2;
  color: #fff;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.5);
}

.wall-text p {
  margin: 4px 0 0;
  color: #c4b5fd;
  font-size: 0.8125rem;
  font-weight: 500;
}

.url-list-alert {
  margin-top: 8px;
}

.url-list-text {
  word-break: break-all;
  font-size: 0.85rem;
}

.primary-gradient-btn {
  background: linear-gradient(90deg, #3b82f6 0%, #8b5cf6 55%, #a855f7 100%) !important;
  color: #fff !important;
  font-weight: 600;
  text-transform: none;
  box-shadow: 0 6px 20px rgba(99, 102, 241, 0.35) !important;
}

.upload-card {
  background: #ffffff;
  color: #0f172a;
}

.upload-header {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 22px 24px 6px;
}

.upload-title-icon {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.35);
}

.upload-header-text {
  flex: 1;
  min-width: 0;
}

.upload-title {
  font-size: 1.25rem;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.2;
}

.upload-hint {
  margin: 6px 0 0;
  font-size: 0.875rem;
  color: #64748b;
  line-height: 1.45;
}

.upload-card-text {
  padding: 8px 24px 4px !important;
}

.upload-form-fields {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.upload-form-fields :deep(.upload-field) {
  width: 100%;
  flex: 1 1 auto;
}

.upload-form-fields :deep(.upload-field .v-input__control) {
  width: 100%;
}

.upload-form-fields :deep(.upload-field .v-field) {
  border-radius: 12px;
}

.upload-form-fields :deep(.upload-field--file .v-input__prepend) {
  display: none !important;
  margin: 0 !important;
  width: 0 !important;
  min-width: 0 !important;
  padding: 0 !important;
}

.upload-form-fields :deep(.upload-field--file .v-input) {
  --v-input-padding-start: 0px;
}

.upload-form-fields :deep(.v-field__prepend-inner) {
  opacity: 0.7;
}

.upload-preview {
  border-radius: 14px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  width: 100%;
}

.upload-actions {
  padding: 12px 24px 22px;
}

:deep(.home-snackbar .v-snackbar__wrapper) {
  background: linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%) !important;
  color: #fff;
  font-weight: 500;
}

@media (max-width: 640px) {
  .home-header {
    flex-direction: column;
    align-items: stretch;
  }

  .header-actions {
    justify-content: stretch;
  }

  .header-actions .v-btn {
    flex: 1;
  }

  .grid-wrap {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 14px;
  }
}
</style>
